import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  TextField,
  Button,
  Grid,
  Alert,
  MenuItem,
  Card,
  CardContent,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  Divider,
} from '@mui/material';
import { useAuth } from '../../context/AuthContext';
import api from '../../services/api';

const LoanApplicationForExisting = () => {
  const { user } = useAuth();
  const [customers, setCustomers] = useState([]);
  const [filteredCustomers, setFilteredCustomers] = useState([]);
  const [loanTemplates, setLoanTemplates] = useState([]);
  const [existingLoans, setExistingLoans] = useState(null);
  const [loadingExistingLoans, setLoadingExistingLoans] = useState(false);
  const [customerLoans, setCustomerLoans] = useState([]);
  const [loadingLoans, setLoadingLoans] = useState(false);
  const [formData, setFormData] = useState({
    customerId: '',
    loanTemplateId: '',
    notes: '',
  });
  const [message, setMessage] = useState('');
  const [nicFilter, setNicFilter] = useState('');
  const [loadingCustomers, setLoadingCustomers] = useState(false);

  useEffect(() => {
    fetchLoanTemplates();
    fetchCustomers();
  }, []);

  useEffect(() => {
    // Debounce the NIC filter to avoid too many API calls
    const timer = setTimeout(() => {
      fetchCustomers();
    }, 300); // Wait 300ms after user stops typing

    return () => clearTimeout(timer);
  }, [nicFilter]);

  useEffect(() => {
    if (formData.customerId) {
      fetchExistingLoans();
      fetchCustomerLoans();
    } else {
      setExistingLoans(null);
      setCustomerLoans([]);
    }
  }, [formData.customerId]);

  const fetchCustomers = async () => {
    try {
      setLoadingCustomers(true);
      const params = new URLSearchParams();
      params.append('type', 'EXISTING_OR_CLOSED'); // Unified type
      if (nicFilter && nicFilter.trim()) {
        params.append('nic', nicFilter.trim());
      }
      
      const response = await api.get(`/officer/customers?${params.toString()}`);
      const customersWithLoans = response.data || [];
      
      setCustomers(customersWithLoans);
      setFilteredCustomers(customersWithLoans);
    } catch (error) {
      console.error('Error fetching customers:', error);
      setCustomers([]);
      setFilteredCustomers([]);
      setMessage('Error fetching customers: ' + (error.response?.data?.message || error.message));
    } finally {
      setLoadingCustomers(false);
    }
  };

  const fetchExistingLoans = async () => {
    if (!formData.customerId) return;
    setLoadingExistingLoans(true);
    try {
      const response = await api.get(`/officer/customers/${formData.customerId}/existing-loans`);
      setExistingLoans(response.data || { loans: [], totalRemainingAmount: 0 });
    } catch (error) {
      console.error('Error fetching existing loans:', error);
      setExistingLoans(null);
    } finally {
      setLoadingExistingLoans(false);
    }
  };

  const fetchCustomerLoans = async () => {
    if (!formData.customerId) return;
    setLoadingLoans(true);
    try {
      const response = await api.get(`/officer/customers/${formData.customerId}/loans`);
      
      // Handle different response structures
      let loansArray = [];
      if (response.data) {
        if (Array.isArray(response.data)) {
          loansArray = response.data;
        } else if (response.data.loans && Array.isArray(response.data.loans)) {
          loansArray = response.data.loans;
        } else if (response.data.loans && !Array.isArray(response.data.loans)) {
          // Single loan object
          loansArray = [response.data.loans];
        }
      }
      
      console.log('Fetched customer loans:', loansArray.length, loansArray);
      setCustomerLoans(loansArray);
      
      // Check if customer has arrears and show immediate alert
      const hasArrears = loansArray.some(loan => loan.closedByAdmin && loan.arrearsAmount && loan.arrearsAmount > 0);
      if (hasArrears) {
        const totalArrears = loansArray
          .filter(loan => loan.closedByAdmin && loan.arrearsAmount && loan.arrearsAmount > 0)
          .reduce((sum, loan) => sum + (parseFloat(loan.arrearsAmount) || 0), 0);
        setMessage(`⚠️ WARNING: This customer has outstanding arrears of Rs ${totalArrears.toFixed(2)} from previous admin-closed loans. Please review before proceeding.`);
      }
    } catch (error) {
      console.error('Error fetching customer loans:', error);
      console.error('Error response:', error.response?.data);
      setCustomerLoans([]);
      // Don't show error message for 404, just log it
      if (error.response?.status !== 404) {
        setMessage('Error fetching loan history: ' + (error.response?.data?.error || error.message));
      }
    } finally {
      setLoadingLoans(false);
    }
  };

  const fetchLoanTemplates = async () => {
    try {
      const response = await api.get('/officer/loan-templates');
      setLoanTemplates(response.data);
    } catch (error) {
      console.error('Error fetching loan templates:', error);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.customerId || !formData.loanTemplateId) {
      setMessage('Please select a customer and loan template');
      return;
    }

    // Determine which endpoint to use based on customer's loan status
    const hasActiveLoans = existingLoans?.hasActiveLoans || false;
    const allLoansClosed = customerLoans.length > 0 && 
      customerLoans.every(loan => loan.status === 'CLOSED' || loan.status === 'REJECTED');

    try {
      let loanData;
      let endpoint;
      let successMessage;

      if (hasActiveLoans) {
        // Customer has active loans - close them and create new loan
        loanData = {
          notes: formData.notes,
        };
        endpoint = '/officer/loans/with-deduction';
        successMessage = 'Loan application created successfully! Previous active loans have been closed and a new loan has been created.';
      } else if (allLoansClosed) {
        // All loans are closed - create fresh new loan
        const selectedTemplate = loanTemplates.find(t => t.id === parseInt(formData.loanTemplateId));
        loanData = {
          requestedAmount: selectedTemplate?.loanAmount || 0,
          notes: formData.notes || '[Loan for customer with all previous loans closed]',
        };
        endpoint = '/officer/loans';
        successMessage = 'Loan application created successfully! This is a fresh loan for a customer with all previous loans closed.';
      } else {
        // Customer has pending/approved loans but no active loans
        const selectedTemplate = loanTemplates.find(t => t.id === parseInt(formData.loanTemplateId));
        loanData = {
          requestedAmount: selectedTemplate?.loanAmount || 0,
          notes: formData.notes,
        };
        endpoint = '/officer/loans';
        successMessage = 'Loan application created successfully!';
      }

      await api.post(endpoint, loanData, {
        params: {
          customerId: formData.customerId,
          officerId: user.userId,
          ...(endpoint === '/officer/loans/with-deduction' && { loanTemplateId: formData.loanTemplateId }),
        },
      });
      
      setMessage(successMessage);
      setFormData({
        customerId: '',
        loanTemplateId: '',
        notes: '',
      });
      setNicFilter('');
      setExistingLoans(null);
      setCustomerLoans([]);
      fetchCustomers(); // Refresh customer list
    } catch (error) {
      setMessage('Error creating loan application: ' + (error.response?.data?.error || error.response?.data?.message || error.message));
    }
  };

  // Separate loans into active and closed
  const activeLoans = customerLoans.filter(loan => loan.status === 'ACTIVE');
  const closedLoans = customerLoans.filter(loan => loan.status === 'CLOSED');
  const otherLoans = customerLoans.filter(loan => 
    loan.status !== 'ACTIVE' && loan.status !== 'CLOSED' && loan.status !== 'REJECTED'
  );

  return (
    <Box>
      <Typography variant="h4" gutterBottom>
        Create Loan for Existing Customer
      </Typography>
      <Alert severity="info" sx={{ mb: 2 }}>
        This page is for customers who have existing loans (either active or all closed). 
        When creating a loan for customers with active loans, those loans will be automatically closed.
      </Alert>
      <Paper sx={{ p: 3, mt: 2 }}>
        {message && (
          <Alert 
            severity={message.includes('Error') || message.includes('WARNING') ? 'warning' : message.includes('success') ? 'success' : 'info'} 
            sx={{ mb: 2 }} 
            onClose={() => setMessage('')}
          >
            {message}
          </Alert>
        )}
        <form onSubmit={handleSubmit}>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Search by NIC"
                value={nicFilter}
                onChange={(e) => setNicFilter(e.target.value)}
                placeholder="Enter NIC to search customers"
                sx={{ mb: 2 }}
                helperText={loadingCustomers ? 'Searching...' : `Found ${filteredCustomers.length} customer(s)`}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                select
                label="Customer"
                required
                value={formData.customerId}
                onChange={(e) => setFormData({ ...formData, customerId: e.target.value })}
                disabled={loadingExistingLoans}
                helperText={filteredCustomers.length > 0 ? "Select a customer with existing loans" : "No customers with existing loans available"}
              >
                {filteredCustomers.length === 0 ? (
                  <MenuItem disabled>No customers available</MenuItem>
                ) : (
                  filteredCustomers.map((customer) => (
                    <MenuItem key={customer.id} value={customer.id}>
                      {customer.fullName || customer.name} - {customer.nic}
                    </MenuItem>
                  ))
                )}
              </TextField>
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                select
                label="Loan Template"
                required
                value={formData.loanTemplateId}
                onChange={(e) => setFormData({ ...formData, loanTemplateId: e.target.value })}
                disabled={!formData.customerId || loadingExistingLoans}
              >
                {loanTemplates.map((template) => (
                  <MenuItem key={template.id} value={template.id}>
                    {template.name} - Rs {template.loanAmount?.toFixed(2)} ({template.numberOfInstallments} installments)
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Notes"
                multiline
                rows={4}
                value={formData.notes}
                onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
              />
            </Grid>
            <Grid item xs={12}>
              <Button 
                type="submit" 
                variant="contained" 
                size="large"
                disabled={!formData.customerId || !formData.loanTemplateId || loadingExistingLoans}
              >
                Submit Loan Application
              </Button>
            </Grid>
          </Grid>
        </form>

        {/* Customer Arrears Summary - Prominent Display */}
        {formData.customerId && customerLoans.length > 0 && (() => {
          const adminClosedLoansWithArrears = customerLoans.filter(
            loan => loan.closedByAdmin && loan.arrearsAmount && loan.arrearsAmount > 0
          );
          const totalArrears = adminClosedLoansWithArrears.reduce(
            (sum, loan) => sum + (parseFloat(loan.arrearsAmount) || 0), 0
          );
          
          if (adminClosedLoansWithArrears.length > 0) {
            return (
              <Box sx={{ mt: 3 }}>
                <Card sx={{ bgcolor: 'error.light', border: '2px solid', borderColor: 'error.main' }}>
                  <CardContent>
                    <Typography variant="h6" gutterBottom sx={{ color: 'error.dark', fontWeight: 'bold' }}>
                      ⚠️ Special Status: Customer Has Outstanding Arrears
                    </Typography>
                    <Alert severity="error" sx={{ mb: 2, mt: 1 }}>
                      <Typography variant="body1" sx={{ fontWeight: 'bold', mb: 1 }}>
                        This customer had {adminClosedLoansWithArrears.length} loan(s) closed by admin with outstanding arrears.
                      </Typography>
                      <Typography variant="h6" sx={{ color: 'error.dark', fontWeight: 'bold' }}>
                        Total Outstanding Arrears: Rs {totalArrears.toFixed(2)}
                      </Typography>
                      <Typography variant="body2" sx={{ mt: 1 }}>
                        Please review the loan history below for detailed information before proceeding with the loan application.
                      </Typography>
                    </Alert>
                    <Box sx={{ mt: 2 }}>
                      <Typography variant="subtitle2" sx={{ fontWeight: 'bold', mb: 1 }}>
                        Loans with Arrears:
                      </Typography>
                      {adminClosedLoansWithArrears.map((loan, idx) => (
                        <Typography key={idx} variant="body2" sx={{ ml: 2 }}>
                          • Loan #{loan.id}: Rs {loan.arrearsAmount?.toFixed(2) || '0.00'} 
                          {loan.closedAt && ` (Closed: ${new Date(loan.closedAt).toLocaleDateString()})`}
                          {loan.closedByAdminUser && ` by ${loan.closedByAdminUser.fullName || loan.closedByAdminUser.username}`}
                        </Typography>
                      ))}
                    </Box>
                  </CardContent>
                </Card>
              </Box>
            );
          }
          return null;
        })()}

        {/* Customer Loan Information */}
        {formData.customerId && (
          <Box sx={{ mt: 4 }}>
            {/* Active Loans Section */}
            {!loadingLoans && !loadingExistingLoans && activeLoans.length > 0 && existingLoans && existingLoans.loans && existingLoans.loans.length > 0 && (
              <Card sx={{ mb: 3, bgcolor: 'warning.light' }}>
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    Existing Active Loans (Will be closed)
                  </Typography>
                  <TableContainer>
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell><strong>Loan ID</strong></TableCell>
                          <TableCell><strong>Loan Amount</strong></TableCell>
                          <TableCell><strong>Remaining Installments</strong></TableCell>
                          <TableCell><strong>Remaining Amount</strong></TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {existingLoans.loans.map((loanInfo, index) => (
                          <TableRow key={index}>
                            <TableCell>{loanInfo.loan.id}</TableCell>
                            <TableCell>Rs {loanInfo.loan.approvedAmount?.toFixed(2)}</TableCell>
                            <TableCell>
                              {loanInfo.paidInstallments} / {loanInfo.totalInstallments} paid
                            </TableCell>
                            <TableCell>
                              <strong>Rs {loanInfo.remainingAmount?.toFixed(2)}</strong>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                  <Alert severity="warning" sx={{ mt: 2 }}>
                    <strong>Total Remaining from Existing Loans:</strong> Rs {existingLoans.totalRemainingAmount?.toFixed(2)}
                    <br />
                    <strong>Note:</strong> These active loans will be automatically closed when you submit the new loan application.
                  </Alert>
                </CardContent>
              </Card>
            )}

            {/* Closed Loans Section */}
            {!loadingLoans && closedLoans.length > 0 && (
              <Card sx={{ mb: 3, bgcolor: 'info.light' }}>
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    Closed Loans ({closedLoans.length})
                  </Typography>
                  <TableContainer>
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell><strong>Loan ID</strong></TableCell>
                          <TableCell><strong>Status</strong></TableCell>
                          <TableCell><strong>Loan Amount</strong></TableCell>
                          <TableCell><strong>Arrears</strong></TableCell>
                          <TableCell><strong>Closed Date</strong></TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {closedLoans.map((loan) => (
                          <TableRow key={loan.id}>
                            <TableCell>{loan.id}</TableCell>
                            <TableCell>
                              <Chip
                                label={loan.status}
                                color="default"
                                size="small"
                              />
                            </TableCell>
                            <TableCell>Rs {loan.approvedAmount?.toFixed(2) || loan.requestedAmount?.toFixed(2) || '0.00'}</TableCell>
                            <TableCell>
                              {loan.closedByAdmin && loan.arrearsAmount && loan.arrearsAmount > 0 ? (
                                <Chip 
                                  label={`Rs ${loan.arrearsAmount.toFixed(2)}`}
                                  color="error"
                                  size="small"
                                  title="This loan was closed by admin with arrears"
                                />
                              ) : loan.closedByAdmin ? (
                                <Chip 
                                  label="Closed by Admin"
                                  color="warning"
                                  size="small"
                                />
                              ) : (
                                '-'
                              )}
                            </TableCell>
                            <TableCell>
                              {loan.closedAt ? new Date(loan.closedAt).toLocaleDateString() : 
                               loan.updatedAt ? new Date(loan.updatedAt).toLocaleDateString() : 
                               'N/A'}
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                  <Alert severity="info" sx={{ mt: 2 }}>
                    This customer has {closedLoans.length} closed loan(s). This will be a fresh new loan application.
                  </Alert>
                </CardContent>
              </Card>
            )}

            {/* Special Status Alert */}
            {!loadingLoans && customerLoans.some(loan => loan.closedByAdmin && loan.arrearsAmount > 0) && (
              <Alert severity="warning" sx={{ mb: 2 }}>
                <strong>Special Status:</strong> This customer had previous loans closed by admin with arrears. 
                Please review the loan history below for details.
              </Alert>
            )}

            {/* Complete Loan History - Always visible when customer is selected */}
            <Typography variant="h6" gutterBottom sx={{ mt: 3 }}>
              Complete Loan History
            </Typography>
            {loadingLoans ? (
              <Alert severity="info">Loading loan history...</Alert>
            ) : customerLoans.length === 0 ? (
              <Alert severity="info">No loan history found for this customer.</Alert>
            ) : (
              <TableContainer component={Paper} sx={{ mt: 2 }}>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell><strong>Loan ID</strong></TableCell>
                      <TableCell><strong>Status</strong></TableCell>
                      <TableCell><strong>Requested Amount</strong></TableCell>
                      <TableCell><strong>Approved Amount</strong></TableCell>
                      <TableCell><strong>Arrears</strong></TableCell>
                      <TableCell><strong>Created Date</strong></TableCell>
                      <TableCell><strong>Closed Date</strong></TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {customerLoans.map((loan) => (
                      <TableRow key={loan.id}>
                        <TableCell>{loan.id}</TableCell>
                        <TableCell>
                          <Chip
                            label={loan.status}
                            color={
                              loan.status === 'ACTIVE' ? 'success' :
                              loan.status === 'APPROVED' ? 'info' :
                              loan.status === 'PENDING' ? 'warning' :
                              loan.status === 'REJECTED' ? 'error' :
                              loan.status === 'CLOSED' ? 'default' : 'default'
                            }
                            size="small"
                          />
                        </TableCell>
                        <TableCell>Rs {loan.requestedAmount?.toFixed(2) || '0.00'}</TableCell>
                        <TableCell>Rs {loan.approvedAmount?.toFixed(2) || 'N/A'}</TableCell>
                        <TableCell>
                          {loan.closedByAdmin && loan.arrearsAmount && loan.arrearsAmount > 0 ? (
                            <Chip 
                              label={`Rs ${loan.arrearsAmount.toFixed(2)}`}
                              color="error"
                              size="small"
                              title="This loan was closed by admin with arrears"
                            />
                          ) : loan.closedByAdmin ? (
                            <Chip 
                              label="Closed by Admin"
                              color="warning"
                              size="small"
                            />
                          ) : (
                            '-'
                          )}
                        </TableCell>
                        <TableCell>
                          {loan.createdAt ? new Date(loan.createdAt).toLocaleDateString() : 'N/A'}
                        </TableCell>
                        <TableCell>
                          {loan.status === 'CLOSED' ? (
                            loan.closedAt ? new Date(loan.closedAt).toLocaleDateString() : 
                            loan.updatedAt ? new Date(loan.updatedAt).toLocaleDateString() : 
                            'N/A'
                          ) : '-'}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            )}

            {/* Summary Information */}
            {!loadingLoans && activeLoans.length > 0 && closedLoans.length > 0 && (
              <Alert severity="info" sx={{ mt: 2 }}>
                <strong>Summary:</strong> This customer has {activeLoans.length} active loan(s) and {closedLoans.length} closed loan(s). 
                When you submit, the active loans will be closed and a new loan will be created.
              </Alert>
            )}
            {!loadingLoans && activeLoans.length === 0 && closedLoans.length > 0 && (
              <Alert severity="success" sx={{ mt: 2 }}>
                All previous loans for this customer are closed. This will be a fresh new loan application.
              </Alert>
            )}
          </Box>
        )}
      </Paper>
    </Box>
  );
};

export default LoanApplicationForExisting;
